// UsersManagement: Super Admin user management page
import React, { useEffect, useState } from 'react';
import SuperAdminLayout from '../../layouts/SuperAdminLayout';
import AdminLayout from '../../layouts/AdminLayout';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, CircularProgress, Switch, Chip, IconButton, FormControlLabel, Tooltip, useTheme, Autocomplete, FormControl, FormLabel, RadioGroup, Radio, Grid, Collapse, Snackbar, Alert } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import api from '../../services/api';
import { useProfile } from '../../context/ProfileContext';

function UsersManagement() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [confirmDelete, setConfirmDelete] = useState({ open: false, user: null });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', role: 'staff', status: 'active', permissions: [], paymentRole: 'none', assignedAreas: [] });
  const [saving, setSaving] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [venues, setVenues] = useState([]);
  const [areaDialogOpen, setAreaDialogOpen] = useState(false);
  const [areaDialogIndex, setAreaDialogIndex] = useState(null);
  const [areaDialogForm, setAreaDialogForm] = useState({ area: '', permissions: [] });
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const { profile } = useProfile() || {};
  const currentUser = profile || (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || '{}') : {});
  const isSuperAdmin = currentUser.role === 'superadmin';
  const isAdmin = currentUser.role === 'admin';
  const staffPermissions = [
    { value: 'booking', label: 'Booking Management (create/view/approve)' },
    { value: 'packages', label: 'Packages (view/manage)' },
    { value: 'booth', label: 'Booth Management' },
    { value: 'cms', label: 'CMS (pages, banners)' },
    { value: 'notifications', label: 'Notifications (view/send)' },
    { value: 'reports', label: 'Reports (view/export)' },
    { value: 'tickets', label: 'Tickets (create/manage)' },
    { value: 'storage', label: 'Files / Storage' },
    { value: 'analytics', label: 'Analytics (read-only)' },
  ];
  
  // Frontend -> Backend mapping:
  // - `form.permissions` (array of strings) -> persisted to `User.permissions` on backend
  // - `form.assignedAreas` (array of { area, permissions }) -> persisted to `User.assignedAreas`
  // - `form.paymentRole` -> persisted to `User.paymentRole`
  // Note: frontend saves these fields; backend must enforce them for actual permission checks.

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem('token');
    api.get('/api/users', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        // Accept both array and object (with users property)
        let allUsers = Array.isArray(res.data) ? res.data : (Array.isArray(res.data.users) ? res.data.users : []);
        // Only show system staff (exclude bookers)
        // Assume bookers have role 'booker' or any role not in ['superadmin','admin','staff']
        const systemRoles = ['superadmin', 'admin', 'staff'];
        const filteredUsers = allUsers.filter(u => systemRoles.includes(u.role));
        setUsers(filteredUsers);
      })
      .catch(err => setError(err.response?.data?.error || 'Failed to fetch users'))
      .finally(() => setLoading(false));
  }, []);

  // fetch venues for Assigned Areas dropdown (preload)
  useEffect(() => {
    let mounted = true;
    api.get('/api/venues')
      .then(res => { if (mounted) setVenues(Array.isArray(res.data) ? res.data : (Array.isArray(res.data.venues) ? res.data.venues : [])); })
      .catch(() => { if (mounted) setVenues([]); });
    return () => { mounted = false; };
  }, []);

  const handleDelete = async (id) => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      // Prevent superadmin from deleting themselves
      if (currentUser._id === id) {
        setError('You cannot delete your own account.');
        setLoading(false);
        return;
      }
      await api.delete(`/api/users/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(prev => prev.filter(u => u._id !== id));
      setConfirmDelete({ open: false, user: null });
      // Audit log for delete action
      try {
        await api.post('/api/audit', {
          action: 'delete_user',
          actor: currentUser.email,
          target: id,
          timestamp: new Date().toISOString(),
          details: 'User deleted from UsersManagement page'
        }, { headers: { Authorization: `Bearer ${token}` } });
      } catch (auditErr) {
        // Optionally handle audit error
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    setEditUser(user);
    setForm(user ? { ...user, password: '', permissions: user.permissions || [], paymentRole: user.paymentRole || 'none', assignedAreas: user.assignedAreas || [] } : { name: '', email: '', phone: '', password: '', role: 'staff', status: 'active', permissions: [], paymentRole: 'none', assignedAreas: [] });
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditUser(null);
    setForm({ name: '', email: '', phone: '', password: '', role: 'staff', status: 'active', permissions: [], paymentRole: 'none', assignedAreas: [] });
  };
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'permissions') {
      setForm(f => {
        let perms = f.permissions || [];
        if (checked) perms = [...perms, value];
        else perms = perms.filter(p => p !== value);
        return { ...f, permissions: perms };
      });
      return;
    }

    // If changing role, clear staff-specific fields when role isn't staff
    if (name === 'role') {
      setForm(f => ({ ...f, role: value, permissions: value === 'staff' ? (f.permissions || []) : [], assignedAreas: value === 'staff' ? (f.assignedAreas || []) : [] }));
      return;
    }

    setForm(f => ({ ...f, [name]: value }));
  };
  // Assigned areas handlers
  const addAssignedArea = () => setForm(f => ({ ...f, assignedAreas: [...(f.assignedAreas || []), { area: '', permissions: [] }] }));
  const removeAssignedArea = (index) => setForm(f => ({ ...f, assignedAreas: (f.assignedAreas || []).filter((_, i) => i !== index) }));
  const handleAssignedAreaChange = (index, field, value) => setForm(f => {
    const aa = Array.isArray(f.assignedAreas) ? [...f.assignedAreas] : [];
    aa[index] = { ...(aa[index] || {}), [field]: value };
    return { ...f, assignedAreas: aa };
  });
  const toggleAssignedAreaPermission = (index, perm) => setForm(f => {
    const aa = Array.isArray(f.assignedAreas) ? [...f.assignedAreas] : [];
    const item = aa[index] || { area: '', permissions: [] };
    const perms = new Set(item.permissions || []);
    if (perms.has(perm)) perms.delete(perm); else perms.add(perm);
    aa[index] = { ...item, permissions: Array.from(perms) };
    return { ...f, assignedAreas: aa };
  });

  const validateForm = (payload) => {
    const errs = {};
    if (!payload.name || !payload.name.trim()) errs.name = 'Name is required';
    if (!payload.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) errs.email = 'Valid email is required';
    if (!editUser) {
      if (!payload.password || payload.password.length < 8) errs.password = 'Password is required (min 8 chars)';
    }
    return errs;
  };

  // Permission helpers
  const selectAllPermissions = () => setForm(f => ({ ...f, permissions: staffPermissions.map(p => p.value) }));
  const clearAllPermissions = () => setForm(f => ({ ...f, permissions: [] }));

  // Assigned area dialog handlers to avoid empty inline rows
  const openAddAreaDialog = () => {
    setAreaDialogForm({ area: '', permissions: [] });
    setAreaDialogIndex(null);
    setAreaDialogOpen(true);
  };
  const openEditAreaDialog = (idx) => {
    const existing = (form.assignedAreas || [])[idx] || { area: '', permissions: [] };
    setAreaDialogForm({ area: existing.area || '', permissions: existing.permissions || [] });
    setAreaDialogIndex(idx);
    setAreaDialogOpen(true);
  };
  const closeAreaDialog = () => setAreaDialogOpen(false);
  const saveAreaDialog = () => {
    if (!areaDialogForm.area || !String(areaDialogForm.area).trim()) {
      setFieldErrors(fe => ({ ...fe, assignedAreas: 'Area is required' }));
      return;
    }
    setFieldErrors(fe => ({ ...fe, assignedAreas: undefined }));
    setForm(f => {
      const aa = Array.isArray(f.assignedAreas) ? [...f.assignedAreas] : [];
      const entry = { area: String(areaDialogForm.area).trim(), permissions: Array.from(new Set(areaDialogForm.permissions || [])) };
      if (areaDialogIndex === null) {
        aa.push(entry);
      } else {
        aa[areaDialogIndex] = entry;
      }
      return { ...f, assignedAreas: aa };
    });
    setAreaDialogOpen(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setFieldErrors({});
    try {
      const token = localStorage.getItem('token');
      // normalize assignedAreas to array when sending
      const payload = { ...form };

      // dedupe permissions
      payload.permissions = Array.from(new Set((payload.permissions || []).filter(Boolean)));

      // normalize assignedAreas
      if (typeof payload.assignedAreas === 'string') {
        payload.assignedAreas = payload.assignedAreas.split(',').map(s => ({ area: s.trim(), permissions: [] })).filter(a => a.area);
      }
      if (Array.isArray(payload.assignedAreas)) {
        // check for empty entries
        const hasEmpty = payload.assignedAreas.some(a => !a || !String(a.area || '').trim());
        if (hasEmpty) {
          setFieldErrors({ assignedAreas: 'Please remove empty assigned area entries or select an area.' });
          setSaving(false);
          return;
        }
        const map = new Map();
        for (const a of payload.assignedAreas) {
          const areaKey = String(a.area);
          const perms = Array.from(new Set((a.permissions || []).filter(p => ['view', 'manage'].includes(p))));
          if (map.has(areaKey)) {
            const existing = map.get(areaKey);
            map.set(areaKey, { area: areaKey, permissions: Array.from(new Set([...existing.permissions, ...perms])) });
          } else {
            map.set(areaKey, { area: areaKey, permissions: perms });
          }
        }
        payload.assignedAreas = Array.from(map.values());
      } else {
        payload.assignedAreas = [];
      }

      const validation = validateForm(payload);
      if (Object.keys(validation).length > 0) {
        setFieldErrors(validation);
        setSaving(false);
        return;
      }

      if (editUser) {
        // Edit user
        if (isAdmin && (editUser.role === 'admin' || editUser.role === 'superadmin')) {
          setError('Admins can only edit staff accounts.');
          setSaving(false);
          return;
        }
        const res = await api.put(`/api/users/${editUser._id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        setUsers(prev => prev.map(u => ((u._id === editUser._id) || (u.id === editUser._id)) ? { ...u, ...res.data.user, password: undefined } : u));
      } else {
        // Create user - choose role-specific endpoint and enforce admin limits locally
        // Admins may only create staff; superadmin can create any role
        if (isAdmin && payload.role && payload.role !== 'staff') {
          setError('Admins can only create staff accounts.');
          setSaving(false);
          return;
        }

        // Prepare endpoint based on desired role
        let endpoint = '/api/users';
        const role = (payload.role || 'staff').toLowerCase();
        if (role === 'staff') endpoint = '/api/users/create-staff';
        else if (role === 'admin') endpoint = '/api/users/create-admin';
        else if (role === 'superadmin') endpoint = '/api/users/create-superadmin';

        // Remove UI-only fields and ensure arrays are sent properly
        const sendPayload = {
          name: payload.name,
          email: payload.email,
          phone: payload.phone,
          password: payload.password,
          role: role,
          status: payload.status || 'active'
        };
        if (role === 'staff') {
          sendPayload.permissions = payload.permissions || [];
          sendPayload.assignedAreas = payload.assignedAreas || [];
          if (payload.paymentRole) sendPayload.paymentRole = payload.paymentRole;
        } else if (role === 'admin' || role === 'superadmin') {
          // Allow superadmin to set permissions for admin
          if (payload.permissions && payload.permissions.length && isSuperAdmin) sendPayload.permissions = payload.permissions;
          if ('sensitive' in payload && isSuperAdmin) sendPayload.sensitive = !!payload.sensitive;
        }

        const res = await api.post(endpoint, sendPayload, { headers: { Authorization: `Bearer ${token}` } });
        setUsers(prev => [...prev, res.data.user]);
      }
      // show success snackbar then close dialog
      setSnackbar({ open: true, message: editUser ? 'User updated successfully' : 'User created successfully', severity: 'success' });
      handleCloseDialog();
    } catch (err) {
      // Map backend validation / field errors into UI when available
      const resp = err.response?.data || {};
      if (resp.fieldErrors && typeof resp.fieldErrors === 'object') {
        setFieldErrors(resp.fieldErrors);
        setError(resp.error || 'Validation errors');
      } else if (resp.errors && typeof resp.errors === 'object') {
        setFieldErrors(resp.errors);
        setError(resp.error || 'Validation errors');
      } else if (resp.error) {
        setError('Failed to save user: ' + resp.error);
      } else if (err.message) {
        setError('Failed to save user: ' + err.message);
      } else {
        setError('Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleProfile = (user) => setProfileUser(user);
  const handleStatusToggle = async (user) => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const newStatus = user.status === 'active' ? 'inactive' : 'active';
      const res = await api.put(`/api/users/${user._id}`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(prev => prev.map(u => u._id === user._id ? { ...u, status: res.data?.user?.status || newStatus } : u));
    } catch (err) {
      setError(err.response?.data?.error || 'Status update failed');
    } finally {
      setLoading(false);
    }
  };

  const LayoutComponent = isSuperAdmin ? SuperAdminLayout : AdminLayout;
  return (
    <LayoutComponent>
      <Box sx={{ mb: 4, px: { xs: 1, md: 0 }, bgcolor: isDark ? theme.palette.background.default : '#f5f5f5', borderRadius: 3, boxShadow: isDark ? 4 : 2, transition: 'background 0.2s' }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ color: isDark ? theme.palette.primary.light : theme.palette.primary.main }}>
          User Management
        </Typography>
        {(isSuperAdmin || isAdmin) && (
          <Button variant="contained" color="primary" sx={{ mb: 2, borderRadius: 2, fontWeight: 700, boxShadow: isDark ? 3 : 1 }} onClick={() => handleOpenDialog()}>Add User</Button>
        )}
        {error && <Typography color="error" mb={2} sx={{ fontWeight: 700 }}>{error}</Typography>}
        {loading ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}><CircularProgress size={24} /><Typography sx={{ fontWeight: 500 }}>Loading...</Typography></Box> : (
          <Table sx={{ borderRadius: 2, boxShadow: isDark ? 4 : 2, overflow: 'hidden', bgcolor: isDark ? theme.palette.background.paper : '#fff', transition: 'background 0.2s' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: isDark ? theme.palette.background.paper : '#e3f2fd', transition: 'background 0.2s' }}>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Payment</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Permissions</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Last Update</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>No staff users found.</Typography>
                  </TableCell>
                </TableRow>
              ) : users.map((user) => (
                <TableRow key={user._id || user.email} sx={{ background: user._id === currentUser._id ? (isDark ? theme.palette.action.selected : '#e3f2fd') : undefined, transition: 'background 0.2s' }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 700, color: isDark ? theme.palette.text.primary : '#222' }}>{user.name}</Typography>
                      <IconButton size="small" onClick={() => handleProfile(user)} sx={{ color: isDark ? theme.palette.info.light : theme.palette.info.main }}><InfoIcon fontSize="small" /></IconButton>
                      {isSuperAdmin && (
                        <IconButton size="small" color="primary" onClick={() => handleProfile(user)} title="Profile Actions" sx={{ color: isDark ? theme.palette.primary.light : theme.palette.primary.main }}>
                          <span className="material-icons">manage_accounts</span>
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell><Typography sx={{ color: isDark ? theme.palette.text.secondary : '#444' }}>{user.email}</Typography></TableCell>
                  <TableCell><Chip label={user.role} color={user.role === 'superadmin' ? 'primary' : user.role === 'admin' ? 'secondary' : 'default'} size="small" sx={{ fontWeight: 700 }} /></TableCell>
                  <TableCell>
                    <Chip label={user.paymentRole || 'none'} size="small" sx={{ fontWeight: 700 }} />
                  </TableCell>
                  <TableCell>
                    <Switch checked={user.status === 'active'} onChange={() => handleStatusToggle(user)} color="success" disabled={user.role === 'superadmin' && !isSuperAdmin} />
                    <Chip label={user.status} color={user.status === 'active' ? 'success' : 'default'} size="small" sx={{ fontWeight: 700 }} />
                  </TableCell>
                  <TableCell>
                    {user.role === 'staff' && (user.permissions || []).length > 0
                      ? user.permissions.map((p, idx) => <Chip key={user._id + '-' + p + '-' + idx} label={staffPermissions.find(sp => sp.value === p)?.label || p} size="small" sx={{ mr: 0.5, fontWeight: 700 }} />)
                      : user.role === 'superadmin' ? <Chip label="All" color="primary" size="small" sx={{ fontWeight: 700 }} /> : <Chip label="-" size="small" sx={{ fontWeight: 700 }} />}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {user.updatedAt ? `Updated: ${new Date(user.updatedAt).toLocaleString()}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                      {/* Edit icon */}
                      {(isSuperAdmin || (isAdmin && user.role === 'staff')) && (
                        <Tooltip title="Edit User" arrow>
                          {isSuperAdmin && user._id === currentUser._id ? (
                            <span>
                              <IconButton color="primary" disabled sx={{ bgcolor: isDark ? theme.palette.action.selected : '#e3f2fd', '&:hover': { bgcolor: isDark ? theme.palette.action.hover : '#bbdefb' } }}>
                                <EditIcon />
                              </IconButton>
                            </span>
                          ) : (
                            <IconButton color="primary" onClick={() => handleOpenDialog(user)} sx={{ bgcolor: isDark ? theme.palette.action.selected : '#e3f2fd', '&:hover': { bgcolor: isDark ? theme.palette.action.hover : '#bbdefb' } }}>
                              <EditIcon />
                            </IconButton>
                          )}
                        </Tooltip>
                      )}
                      {/* Delete icon: always visible for superadmin (except self) and admin (for staff) */}
                      {(isSuperAdmin && user._id !== currentUser._id) || (isAdmin && user.role === 'staff') ? (
                        <Tooltip title="Delete User" arrow>
                          <span>
                            <IconButton color="error" onClick={() => setConfirmDelete({ open: true, user })} sx={{ bgcolor: isDark ? theme.palette.error.light : '#ffebee', '&:hover': { bgcolor: isDark ? theme.palette.error.main : '#ffcdd2' } }}>
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                      {/* Profile icon */}
                      <Tooltip title="View Profile" arrow>
                        <IconButton color="secondary" onClick={() => handleProfile(user)} sx={{ bgcolor: isDark ? theme.palette.secondary.light : '#f3e5f5', '&:hover': { bgcolor: isDark ? theme.palette.secondary.main : '#ce93d8' } }}>
                          <PersonIcon />
                        </IconButton>
                      </Tooltip>
                      {/* Status toggle icon */}
                      {(isSuperAdmin || (isAdmin && user.role === 'staff')) && user.role !== 'superadmin' && (
                        <Tooltip title={user.status === 'active' ? 'Set Inactive' : 'Set Active'} arrow>
                          <IconButton color={user.status === 'active' ? 'success' : 'default'} onClick={() => handleStatusToggle(user)} sx={{ bgcolor: user.status === 'active' ? (isDark ? theme.palette.success.light : '#e8f5e9') : (isDark ? theme.palette.action.disabledBackground : '#eeeeee'), '&:hover': { bgcolor: user.status === 'active' ? (isDark ? theme.palette.success.main : '#c8e6c9') : (isDark ? theme.palette.action.hover : '#e0e0e0') } }}>
                            {user.status === 'active' ? <ToggleOnIcon /> : <ToggleOffIcon />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
      {/* Create/Edit User Dialog */}

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
            minHeight: '400px',
            width: '480px',
            m: 'auto',
            p: 0,
            display: 'flex',
            flexDirection: 'column',
          }
        }}>
          <DialogTitle sx={{ bgcolor: isDark ? theme.palette.background.paper : '#e3f2fd', color: theme.palette.primary.main, fontWeight: 700, transition: 'background 0.2s, color 0.2s', px: 3, py: 2 }}>{editUser ? `Edit ${editUser.role ? editUser.role.charAt(0).toUpperCase() + editUser.role.slice(1) : 'User'}` : `Add ${form.role ? form.role.charAt(0).toUpperCase() + form.role.slice(1) : 'User'}`}</DialogTitle>
          <DialogContent dividers sx={{
            bgcolor: isDark ? theme.palette.background.default : '#fafafa',
            transition: 'background 0.2s',
            overflowY: 'auto',
            px: 3,
            py: 2,
            flex: 1,
            minHeight: '320px',
            maxHeight: '60vh',
          }}>
            <TextField autoFocus label="Name" name="name" value={form.name} onChange={handleFormChange} fullWidth sx={{ mb: 2 }} error={!!fieldErrors.name} helperText={fieldErrors.name || ''} inputProps={{ 'aria-label': 'name' }} />
            <TextField label="Email" name="email" value={form.email} onChange={handleFormChange} fullWidth sx={{ mb: 2 }} error={!!fieldErrors.email} helperText={fieldErrors.email || ''} inputProps={{ 'aria-label': 'email' }} />
            <TextField label="Phone" name="phone" value={form.phone} onChange={handleFormChange} fullWidth sx={{ mb: 2 }} />
            <TextField label="Password" name="password" value={form.password} onChange={handleFormChange} type="password" fullWidth sx={{ mb: 2 }} error={!!fieldErrors.password} helperText={fieldErrors.password || (editUser ? 'Leave blank to keep existing password' : '')} inputProps={{ 'aria-label': 'password' }} />
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel component="legend" sx={{ mb: 1 }}>Role</FormLabel>
              {isSuperAdmin ? (
                <RadioGroup row name="role" value={form.role} onChange={handleFormChange}>
                  <FormControlLabel value="superadmin" control={<Radio />} label="Super Admin" />
                  <FormControlLabel value="admin" control={<Radio />} label="Admin" />
                  <FormControlLabel value="staff" control={<Radio />} label="Staff" />
                </RadioGroup>
              ) : (
                // Admins can only create staff: show read-only label to avoid confusion
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="Staff" size="small" color="default" />
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Admins may only create staff accounts</Typography>
                </Box>
              )}
            </FormControl>
            {/* Sensitive Actions switch for admin role, only superadmin can toggle */}
            {form.role === 'admin' && (
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={<Switch
                    checked={form.sensitive || false}
                    onChange={e => setForm(f => ({ ...f, sensitive: e.target.checked }))}
                    name="sensitive"
                    color="warning"
                    disabled={!isSuperAdmin}
                  />}
                  label={<span style={{ fontWeight: 500, color: !isSuperAdmin ? '#aaa' : undefined }}>Sensitive Actions Permission (Superadmin only)</span>}
                />
              </Box>
            )}
            {/* Only show permissions toggle for staff, and only if creating/editing staff (not admin/superadmin) */}
            {form.role === 'staff' && (isSuperAdmin || isAdmin) && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">Staff Permissions</Typography>
                  <Box>
                    <Button size="small" onClick={() => { setForm(f => ({ ...f, permissions: staffPermissions.map(p => p.value) })); }} sx={{ mr: 1 }}>Select All</Button>
                    <Button size="small" onClick={() => setForm(f => ({ ...f, permissions: [] }))}>Clear</Button>
                  </Box>
                </Box>
                <ToggleButtonGroup
                  value={form.permissions || []}
                  onChange={(e, val) => setForm(f => ({ ...f, permissions: Array.isArray(val) ? val : [] }))}
                  aria-label="staff-permissions"
                  size="small"
                >
                  {staffPermissions.map(perm => (
                    <ToggleButton key={perm.value} value={perm.value} aria-label={perm.value} disabled={isAdmin && !!editUser && (editUser.role === 'admin' || editUser.role === 'superadmin')}>
                      {perm.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Typography variant="caption" display="block" sx={{ mt: 1, color: theme.palette.text.secondary }}>Toggle permissions to grant staff access. Use Advanced to manage payment/areas.</Typography>
              </Box>
            )}

            {/* Advanced options toggle */}
            <Box sx={{ mb: 2 }}>
              <Button size="small" onClick={() => setAdvancedOpen(a => !a)}>{advancedOpen ? 'Hide Advanced' : 'Show Advanced Options'}</Button>
            </Box>

              {/* Payment role & Assigned Areas: only editable by superadmin (keep form simple for admins) */}
              <Collapse in={advancedOpen}>
                {currentUser.role === 'superadmin' && (
                  <>
                    <TextField select label="Payment Role" name="paymentRole" value={form.paymentRole || 'none'} onChange={handleFormChange} fullWidth sx={{ mb: 2 }}>
                      <MenuItem value="none">None</MenuItem>
                      <MenuItem value="view">View</MenuItem>
                      <MenuItem value="manage">Manage</MenuItem>
                    </TextField>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Assigned Areas & Scopes</Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        {(form.assignedAreas || []).map((aa, idx) => (
                          <Chip key={idx} label={`${aa.area} ${(aa.permissions && aa.permissions.length) ? `(${aa.permissions.join(',')})` : ''}`} onClick={() => openEditAreaDialog(idx)} onDelete={() => setForm(f => ({ ...f, assignedAreas: (f.assignedAreas||[]).filter((_,i)=>i!==idx) }))} sx={{ mr: 1, mb: 1 }} />
                        ))}
                        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openAddAreaDialog}>Add Area</Button>
                      </Box>
                      <Typography variant="caption" display="block" sx={{ mt: 1, color: theme.palette.text.secondary }}>Assigned areas appear as chips — click to edit, or remove using the chip delete action. Use Add Area to create a scoped area.</Typography>
                      {fieldErrors.assignedAreas && <Typography color="error" variant="caption" display="block">{fieldErrors.assignedAreas}</Typography>}
                      {/* Area dialog for add/edit */}
                    <Dialog open={areaDialogOpen} onClose={closeAreaDialog} maxWidth="sm" fullWidth>
                      <DialogTitle>{areaDialogIndex === null ? 'Add Assigned Area' : 'Edit Assigned Area'}</DialogTitle>
                      <DialogContent>
                        <Autocomplete
                          options={venues}
                          getOptionLabel={(o) => o.name || o}
                          value={venues.find(v => v._id === areaDialogForm.area) || (areaDialogForm.area ? { _id: areaDialogForm.area, name: areaDialogForm.area } : null)}
                          onChange={(e, val) => setAreaDialogForm(f => ({ ...f, area: val ? (val._id || val.name) : '' }))}
                          freeSolo
                          renderInput={(params) => <TextField {...params} label="Area" fullWidth sx={{ mt: 1 }} />}
                        />
                        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                          <FormControlLabel control={<Switch checked={(areaDialogForm.permissions||[]).includes('view')} onChange={() => setAreaDialogForm(f => ({ ...f, permissions: (f.permissions||[]).includes('view') ? f.permissions.filter(p=>p!=='view') : [...(f.permissions||[]),'view'] }))} />} label="View" />
                          <FormControlLabel control={<Switch checked={(areaDialogForm.permissions||[]).includes('manage')} onChange={() => setAreaDialogForm(f => ({ ...f, permissions: (f.permissions||[]).includes('manage') ? f.permissions.filter(p=>p!=='manage') : [...(f.permissions||[]),'manage'] }))} />} label="Manage" />
                        </Box>
                      </DialogContent>
                      <DialogActions>
                        <Button onClick={closeAreaDialog}>Cancel</Button>
                        <Button onClick={saveAreaDialog} variant="contained">Save</Button>
                      </DialogActions>
                    </Dialog>
                  </Box>
                </>
              )}
              </Collapse>

              <TextField select label="Status" name="status" value={form.status} onChange={handleFormChange} fullWidth sx={{ mb: 2 }}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
            {error && <Typography color="error" mb={1}>{error}</Typography>}
          </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: t => `1px solid ${t.palette.divider}`, bgcolor: isDark ? theme.palette.background.paper : '#f5f5f5' }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSave} variant="contained" disabled={saving || Object.keys(fieldErrors).length > 0}>{saving ? <CircularProgress size={20} /> : (editUser ? 'Update' : 'Create')}</Button>
          </DialogActions>

          <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
            <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Dialog>
        {/* Delete Confirmation Dialog */}
        <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, user: null })} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ bgcolor: isDark ? theme.palette.background.paper : '#ffebee', color: theme.palette.error.main, fontWeight: 700, transition: 'background 0.2s, color 0.2s' }}>Confirm Delete</DialogTitle>
          <DialogContent sx={{ bgcolor: isDark ? theme.palette.background.default : '#fff8f8', transition: 'background 0.2s' }}>
            <Typography sx={{ color: theme.palette.text.primary }}>
              Are you sure you want to delete user <strong>{confirmDelete.user?.name}</strong> ({confirmDelete.user?.email})?
            </Typography>
            <Chip label={confirmDelete.user?.status} color={confirmDelete.user?.status === 'active' ? 'success' : 'default'} size="small" sx={{ mt: 1, fontWeight: 700 }} />
            <Typography color="error" sx={{ mt: 2, fontWeight: 700 }}>This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions sx={{ bgcolor: isDark ? theme.palette.background.paper : '#ffebee', transition: 'background 0.2s' }}>
            <Button onClick={() => setConfirmDelete({ open: false, user: null })} sx={{ fontWeight: 700 }}>Cancel</Button>
            <Button onClick={() => handleDelete(confirmDelete.user._id)} color="error" variant="contained" sx={{ fontWeight: 700 }}>Delete</Button>
          </DialogActions>
        </Dialog>
        {/* Profile Detail Pop-up */}
        <Dialog open={!!profileUser} onClose={() => setProfileUser(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ bgcolor: isDark ? theme.palette.background.paper : '#f3e5f5', color: theme.palette.secondary.main, fontWeight: 700, transition: 'background 0.2s, color 0.2s' }}>User Profile</DialogTitle>
          <DialogContent sx={{ bgcolor: isDark ? theme.palette.background.default : '#fafafa', transition: 'background 0.2s' }}>
            {profileUser && (
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ color: theme.palette.text.primary }}>{profileUser.name}</Typography>
                <Typography sx={{ color: theme.palette.text.secondary }}>Email: {profileUser.email}</Typography>
                <Typography sx={{ color: theme.palette.text.secondary }}>Phone: {profileUser.phone || '-'}</Typography>
                <Typography sx={{ color: theme.palette.text.secondary }}>Role: {profileUser.role}</Typography>
                <Chip label={profileUser.status} color={profileUser.status === 'active' ? 'success' : 'default'} size="small" sx={{ mt: 1, fontWeight: 700 }} />
                {profileUser.role === 'staff' && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>Permissions:</Typography>
                    {(profileUser.permissions || []).length > 0
                      ? profileUser.permissions.map(p => <Chip key={p} label={staffPermissions.find(sp => sp.value === p)?.label || p} size="small" sx={{ mr: 0.5, fontWeight: 700 }} />)
                      : <Typography variant="body2">None</Typography>}
                  </Box>
                )}
                {profileUser.role === 'superadmin' && (
                  <Typography variant="body2" color="primary">Full system access</Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ bgcolor: isDark ? theme.palette.background.paper : '#f3e5f5', transition: 'background 0.2s' }}>
            <Button onClick={() => setProfileUser(null)} sx={{ fontWeight: 700 }}>Close</Button>
          </DialogActions>
        </Dialog>
    </LayoutComponent>
  );
}

export default UsersManagement;
